import { logger } from '@/lib/logger';
import { requireSupabase } from '@/lib/supabase';
import type { ApprovalAction } from '@/services/wound.service';

export interface WoundApprovalAuditEntry {
    id: string;
    woundAssessmentId: string;
    facilityId: string;
    approverStaffId: string | null;
    approverUserId: string | null;
    approverRole: string | null;
    approverName: string;
    approverLanr: string | null;
    approverSpecialty: string | null;
    action: ApprovalAction;
    oldStatus: string | null;
    newStatus: string | null;
    diagnosisCode: string | null;
    diagnosisName: string | null;
    approvalReason: string | null;
    notes: string | null;
    sessionId: string | null;
    deviceInfo: string | null;
    userAgent: string | null;
    appVersion: string | null;
    createdAt: string;
}

export interface LogWoundApprovalInput {
    woundAssessmentId: string;
    facilityId: string;
    approverStaffId?: string | null;
    approverUserId?: string | null;
    approverRole?: string | null;
    approverName: string;
    approverLanr?: string | null;
    approverSpecialty?: string | null;
    action: ApprovalAction;
    oldStatus?: string | null;
    newStatus?: string | null;
    diagnosisCode?: string | null;
    diagnosisName?: string | null;
    approvalReason?: string | null;
    notes?: string | null;
}

const SESSION_STORAGE_KEY = 'mediflow:wound-approval-session';

function getSessionId(): string | null {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return null;
    }

    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
        return existing;
    }

    const generated = globalThis.crypto?.randomUUID?.() ?? `wound-${Date.now()}`;
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, generated);
    return generated;
}

function getDeviceInfo(): string | null {
    if (typeof navigator === 'undefined') {
        return null;
    }

    return [navigator.platform, navigator.language].filter(Boolean).join(' · ') || null;
}

function mapAuditEntry(row: Record<string, unknown>): WoundApprovalAuditEntry {
    return {
        id: row.id as string,
        woundAssessmentId: row.wound_assessment_id as string,
        facilityId: row.facility_id as string,
        approverStaffId: (row.approver_staff_id as string) ?? null,
        approverUserId: (row.approver_user_id as string) ?? null,
        approverRole: (row.approver_role as string) ?? null,
        approverName: row.approver_name as string,
        approverLanr: (row.approver_lanr as string) ?? null,
        approverSpecialty: (row.approver_specialty as string) ?? null,
        action: row.action as ApprovalAction,
        oldStatus: (row.old_status as string) ?? null,
        newStatus: (row.new_status as string) ?? null,
        diagnosisCode: (row.diagnosis_code as string) ?? null,
        diagnosisName: (row.diagnosis_name as string) ?? null,
        approvalReason: (row.approval_reason as string) ?? null,
        notes: (row.notes as string) ?? null,
        sessionId: (row.session_id as string) ?? null,
        deviceInfo: (row.device_info as string) ?? null,
        userAgent: (row.user_agent as string) ?? null,
        appVersion: (row.app_version as string) ?? null,
        createdAt: row.created_at as string,
    };
}

export async function logWoundApproval(input: LogWoundApprovalInput): Promise<string | null> {
    const supabase = requireSupabase();

    const { data, error } = await supabase
        .from('wound_approval_audit_log')
        .insert({
            wound_assessment_id: input.woundAssessmentId,
            facility_id: input.facilityId,
            approver_staff_id: input.approverStaffId ?? null,
            approver_user_id: input.approverUserId ?? null,
            approver_role: input.approverRole ?? 'care',
            approver_name: input.approverName,
            approver_lanr: input.approverLanr ?? null,
            approver_specialty: input.approverSpecialty ?? null,
            action: input.action,
            old_status: input.oldStatus ?? null,
            new_status: input.newStatus ?? null,
            diagnosis_code: input.diagnosisCode ?? null,
            diagnosis_name: input.diagnosisName ?? null,
            approval_reason: input.approvalReason ?? null,
            notes: input.notes ?? null,
            session_id: getSessionId(),
            device_info: getDeviceInfo(),
            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
            app_version: null,
        })
        .select('id')
        .maybeSingle();

    if (error) {
        logger.error('woundAudit.log failed', { error: error.message, action: input.action });
        return null;
    }

    return (data?.id as string | undefined) ?? null;
}

export async function getApprovalHistory(assessmentId: string): Promise<WoundApprovalAuditEntry[]> {
    const supabase = requireSupabase();

    const { data, error } = await supabase
        .from('wound_approval_audit_log')
        .select('*')
        .eq('wound_assessment_id', assessmentId)
        .order('created_at', { ascending: false });

    if (error) {
        logger.error('woundAudit.history failed', { error: error.message });
        return [];
    }

    return (data ?? []).map((row) => mapAuditEntry(row as Record<string, unknown>));
}